import { useQuery } from "@tanstack/react-query";
import type { AxiosError } from "axios";
import type { DateTime } from "luxon";
import { useTranslation } from "react-i18next";
import { useAnalytics } from "../../contexts/analytics/hooks";
import { useHospitalData } from "../../contexts/HospitalDataProvider";
import { backendService } from "../../services/backend.service";
import type { LeakageReportResponse } from "../../utils/models";

const leakageReportKey = "leakage-report";
// Backend UIVisibleError keys (detail.message) for invalid date ranges — surfaced verbatim so the
// user sees the specific reason rather than the generic fetch error.
const RANGE_ERROR_KEYS = new Set(["leakage_report.range.too.short", "leakage_report.range.not.whole.weeks"]);

/** Extract the backend UIVisibleError message key from an axios error, if present. */
const getBackendErrorKey = (error: unknown): string | undefined => {
	const detail = (error as AxiosError<{ detail?: { message?: string } }>)?.response?.data?.detail;
	return detail?.message;
};

export type UseLeakageReportArgs = {
	startDate?: DateTime;
	endDate?: DateTime;
	roles?: string[];
	jobs?: string[];
	employmentTypes?: string[];
	nurseId?: number;
};

export const useGetLeakageReport = ({ startDate, endDate, roles, jobs, employmentTypes, nurseId }: UseLeakageReportArgs) => {
	const { chosenDepartment, showErrorSnackbar } = useHospitalData();
	const { t } = useTranslation();
	const { track, trackError } = useAnalytics();
	const deptId = chosenDepartment?.department_id;
	const enabled = !!deptId && !!startDate && !!endDate;

	return useQuery({
		queryKey: [leakageReportKey, deptId, startDate?.toISODate(), endDate?.toISODate(), roles, jobs, employmentTypes, nurseId],
		queryFn: async (): Promise<LeakageReportResponse | null> => {
			if (!deptId || !startDate || !endDate) return null;
			track("leakage_report_fetch_start", { department_id: deptId });
			try {
				const response = await backendService.getLeakageReport({ departmentId: deptId, startDate, endDate, roles, jobs, employmentTypes, nurseId });
				track("leakage_report_fetch_success", { department_id: deptId, row_count: response.data.rows.length });
				return response.data;
			} catch (error) {
				const backendKey = getBackendErrorKey(error);
				// Surface a specific date-range message; fall back to the generic fetch error.
				const messageKey = backendKey && RANGE_ERROR_KEYS.has(backendKey) ? backendKey : "leakage_report.fetch_error";
				trackError("leakage_report_fetch_error", { department_id: deptId, error_key: backendKey ?? messageKey });
				showErrorSnackbar(t(messageKey), {}, error);
				return null;
			}
		},
		throwOnError: false,
		enabled,
	});
};
